import { z } from 'zod';
import {
  BlockedDateRange,
  getDateRangeErrors,
} from '@/components/inputs/dateRangeValidation';
import {
  DEFAULT_TRACKING_ICON_ANCHOR,
  TRACKING_ICON_ANCHORS,
  getTrackingIconAnchor,
  TrackingIcon,
  TrackingIconSlot,
} from './TrackingIcon';

export type TrackingIconFormMode = 'create' | 'edit' | 'image' | 'view';

export const trackingIconPath = (slot: TrackingIconSlot) =>
  `/institutional-web/live-tracking/${slot}-icon`;

const dateSchema = z
  .date({ errorMap: () => ({ message: 'Ingresá una fecha válida' }) })
  .nullable();

export const trackingIconFormSchema = (
  mode: TrackingIconFormMode,
  blockedRanges: readonly BlockedDateRange[] = [],
  availabilityError?: string,
  defaultConflict?: string
) =>
  z.preprocess(
    (values) =>
      values !== null &&
      typeof values === 'object' &&
      'isDefault' in values &&
      values.isDefault === true
        ? { ...values, dateFrom: null, dateTo: null }
        : values,
    z
      .object({
        name: z
          .string()
          .trim()
          .min(1, 'El nombre es requerido')
          .max(100, 'Máximo 100 caracteres'),
        dateFrom: dateSchema,
        dateTo: dateSchema,
        enabled: z.boolean(),
        isDefault: z.boolean().default(false),
        anchor: z
          .enum(TRACKING_ICON_ANCHORS, {
            errorMap: () => ({ message: 'Seleccioná una posición válida' }),
          })
          .default(DEFAULT_TRACKING_ICON_ANCHOR),
        image: z.instanceof(File).nullish(),
      })
      .superRefine((data, ctx) => {
        const checkAvailability =
          data.enabled && (mode === 'create' || mode === 'edit');
        if (!data.isDefault) {
          const errors = getDateRangeErrors(
            { from: data.dateFrom, to: data.dateTo },
            {
              withTime: true,
              allowOpenRange: true,
              blockedRanges: checkAvailability ? blockedRanges : [],
            }
          );
          for (const position of ['from', 'to'] as const) {
            if (errors[position])
              ctx.addIssue({
                code: 'custom',
                path: [position === 'from' ? 'dateFrom' : 'dateTo'],
                message: errors[position],
              });
          }
        }
        if (checkAvailability && data.isDefault && defaultConflict) {
          ctx.addIssue({
            code: 'custom',
            path: ['isDefault'],
            message: defaultConflict,
          });
        }
        if (checkAvailability && availabilityError) {
          ctx.addIssue({
            code: 'custom',
            path: [data.isDefault ? 'isDefault' : 'dateTo'],
            message: availabilityError,
          });
        }
        if ((mode === 'create' || mode === 'image') && !data.image) {
          ctx.addIssue({
            code: 'custom',
            path: ['image'],
            message: 'La imagen es requerida',
          });
        }
        if (data.image) {
          if (!['image/png', 'image/webp'].includes(data.image.type)) {
            ctx.addIssue({
              code: 'custom',
              path: ['image'],
              message: 'Solo se aceptan PNG y WebP',
            });
          }
          if (data.image.size === 0 || data.image.size > 2 * 1024 * 1024) {
            ctx.addIssue({
              code: 'custom',
              path: ['image'],
              message:
                'La imagen debe tener contenido y pesar como máximo 2 MB',
            });
          }
        }
      })
  );

export type TrackingIconFormValues = z.infer<
  ReturnType<typeof trackingIconFormSchema>
>;
export type TrackingIconUpdate = Pick<
  TrackingIcon,
  'name' | 'dateFrom' | 'dateTo' | 'enabled' | 'isDefault' | 'anchor'
>;

export const trackingIconDefaultValues = (
  icon?: TrackingIcon
): TrackingIconFormValues => ({
  name: icon?.name ?? '',
  dateFrom: !icon?.isDefault && icon?.dateFrom ? new Date(icon.dateFrom) : null,
  dateTo: !icon?.isDefault && icon?.dateTo ? new Date(icon.dateTo) : null,
  enabled: icon?.enabled ?? true,
  isDefault: icon?.isDefault ?? false,
  anchor: getTrackingIconAnchor(icon?.anchor),
  image: undefined,
});

export const trackingIconPayload = (
  data: TrackingIconFormValues
): TrackingIconUpdate => ({
  name: data.name.trim(),
  dateFrom: data.isDefault ? null : (data.dateFrom?.toISOString() ?? null),
  dateTo: data.isDefault ? null : (data.dateTo?.toISOString() ?? null),
  enabled: data.enabled,
  isDefault: data.isDefault ?? false,
  anchor: data.anchor ?? DEFAULT_TRACKING_ICON_ANCHOR,
});
